import IdeaProperty from "@/types/IdeaProperty";
import Idea, {fieldEditable} from "@/types/Idea";
import Plain from "@/types/Plain";
import {ReverseInfo} from "@/services/reverseRelationship";
import {asArray} from "@/utils/arrayUtils";
import {travelsAsList} from "@/utils/propertyDefinition";
import {hasDimension} from "@/utils/kanbanDimensions";
import {EnhancedWorkpace} from "@/types/Workspace";
import {canCreateIn} from "@/utils/instanceCreation";

/**
 * Regras do Kanban de um relacionamento — o recorte do tabuleiro aos itens que
 * pertencem a uma instância. Mora fora dos componentes porque o modal (aberto do
 * InstanceView) e a página de rota própria precisam decidir exatamente o mesmo.
 */

/** Ids dos itens relacionados, a partir do _rels da instância dona. */
export function relatedIds(parentInstance: any, property: IdeaProperty): number[] {
    const rel = parentInstance && parentInstance._rels && parentInstance._rels[property.alias]
    return asArray(rel).map((item: any) => Number(item._instance))
}

/**
 * Um relacionamento merece Kanban quando tem vários itens e a coleção de destino tem
 * fluxo — só aí existe o que agrupar em situações. Reverso conta como múltiplo — ver o
 * `travelsAsList`, que é quem guarda essa regra. Relacionamento para a própria coleção fica
 * de fora, senão um modal abriria outro sem fim.
 */
export function hasRelationshipKanban(
    property: IdeaProperty, ownIdeaAlias: string, relatedIdea: Idea | undefined, ideas: Idea[],
    itemCount: number
): boolean {
    if (!property.relationship) return false
    if (!travelsAsList(property)) return false
    if (property.relationship.idea === ownIdeaAlias) return false
    //Basta a coleção de destino ter por onde agrupar. Era "ter fluxo", de quando o quadro só
    //sabia agrupar por situação; hoje uma coleção com um atributo de seleção também tem Kanban.
    //Fora o campo que aponta de volta: ele é o mesmo em todos os itens deste recorte, e um
    //quadro que só teria essa dimensão não tem o que agrupar — o portão precisa saber disso
    //aqui, senão a aba apareceria e o quadro cairia fora no primeiro carregamento.
    const back = property.relationship.reverse
    if (!relatedIdea || !hasDimension(relatedIdea, ideas, back ? [back] : [])) return false
    return itemCount > 0
}

/**
 * Recorte da listagem. No reverso o filho guarda o dono, então filtra-se por esse
 * campo — o match implícito de array do Mongo cobre dono escalar e dono múltiplo, do
 * mesmo jeito que o findOwnedBy faz no backend. No múltiplo direto a lista mora no
 * dono, então vai por ids.
 */
export function relationshipFilter(property: IdeaProperty, parentInstance: any, parentId: number | string): Plain {
    const reverseAlias = property.relationship && property.relationship.reverse
    if (reverseAlias) return {[reverseAlias]: Number(parentId)}
    return {_instance: {$in: relatedIds(parentInstance, property)}}
}

/**
 * No reverso o item já nasce apontando para o dono, senão o servidor recusaria por
 * campo obrigatório. O ownerMultiple decide entre id e [id], como em
 * InstanceReverseRelInput.ownerValueFor. No múltiplo direto não há o que preencher:
 * quem guarda a lista é o dono, e o vínculo é feito depois de criar.
 */
export function relationshipInitialValues(
    property: IdeaProperty, parentId: number | string, info: ReverseInfo | null | undefined
): Plain | null {
    const reverseAlias = property.relationship && property.relationship.reverse
    if (!reverseAlias) return null
    const id = Number(parentId)
    return {[reverseAlias]: info && info.ownerMultiple ? [id] : id}
}

/**
 * Onde moram as preferências do quadro deste relacionamento: dimensões, raias, campos e prazos.
 *
 * Por PROPRIEDADE, e não por item: as tarefas de todos os projetos se olham da mesma forma, e
 * configurar uma a uma seria trabalho sem fim. E separado da coleção de destino, que é o que o
 * quadro recebe como contexto — sem isto, o Kanban das tarefas de um projeto e a página da
 * coleção Tarefas dividiriam a mesma configuração e se sobrescreveriam.
 */
export function relationshipScope(ownerIdeaAlias: string, propertyAlias: string): string {
    return `${ownerIdeaAlias}.${propertyAlias}`
}

/** Só o múltiplo direto precisa de vínculo depois de criar. */
export function needsLinkAfterCreate(property: IdeaProperty): boolean {
    return !!property.relationship && !property.relationship.reverse
}

type OwnerUpdateApi = {
    update: (params: { systemAlias: string, ideaAlias: string, instanceId: number }, changes: any) => Promise<any>
}

/**
 * Acrescenta itens ao relacionamento de um dono: os que ele já tem, mais estes.
 *
 * Gravado pelo DONO nos dois casos. No múltiplo direto a lista mora nele; no reverso o servidor
 * aceita o alias reverso no dono e grava o outro lado — é o mesmo caminho que a edição do dono usa.
 *
 * Um dono para os dois que vinculam: o filho recém-criado pelo quadro de relacionamento, e o
 * "Adicionar" da seção na ficha, que cria ou escolhe existentes. Era um PATCH escrito no quadro, e o
 * segundo seria a cópia.
 *
 * Os atuais saem do `_rels` do dono que a tela carregou; quem chama relê o dono depois.
 */
export async function linkToOwner(
    instances: OwnerUpdateApi, ownerContext: OwnerContext, owner: any, property: IdeaProperty, ids: number[]
) {
    const current = relatedIds(owner, property)
    await writeOwnerLinks(instances, ownerContext, property,
        [...current, ...ids.map(Number).filter(id => !current.includes(id))])
}

/**
 * Tira um item do relacionamento de um dono: o X da linha e do card na ficha. A outra metade do
 * `linkToOwner`, pelo mesmo caminho — a lista do dono sem o item, gravada por ele; no reverso o
 * servidor solta o outro lado.
 */
export async function unlinkFromOwner(
    instances: OwnerUpdateApi, ownerContext: OwnerContext, owner: any, property: IdeaProperty, id: number
) {
    await writeOwnerLinks(instances, ownerContext, property,
        relatedIds(owner, property).filter(current => current !== Number(id)))
}

/**
 * O que se pode fazer com o relacionamento de um dono, pelo dono — vincular, criar já vinculado e
 * desvincular. Um dono para os dois lugares que mostram o relacionamento: a Lista da seção na ficha e
 * o quadro (`RelationshipBoard`), que é o mesmo na ficha e na página expandida.
 *
 * - **Vincular** grava no dono. Não quando o dono faz parte da CHAVE do outro lado: trazer um item de
 *   outro dono mudaria o identificador dele, e a edição do dono recusa pelo mesmo motivo.
 * - **Criar** exige poder criar lá; no múltiplo direto, também gravar aqui — o vínculo vem depois, no
 *   dono, e um item que não se vincularia ficaria solto. O item novo já nasce com a chave certa.
 * - **Desvincular** grava no dono, quando o servidor não recusaria (`unlinkable`).
 *
 * Nada disso no relacionamento calculado: a lista é a da fórmula, e o servidor recusaria a gravação. Sem
 * isto, a aba dele oferecia o "Adicionar" e o X.
 */
export function relationshipEditing(
    workspace: EnhancedWorkpace, owner: any, property: IdeaProperty, info: ReverseInfo | null | undefined
) {
    if (property.formula) return {link: false, create: false, unlink: false}
    //O item e o campo (`fieldEditable` pergunta os dois): o relacionamento pode estar travado para
    //quem olha, como qualquer campo
    const ownerEditable = workspace.can('editor') && fieldEditable(owner || {}, property)
    return {
        link: ownerEditable && !(info && info.ownerIdentifier),
        create: canCreateIn(workspace, workspace.ideaWith(property.relationship!.idea))
            && (!needsLinkAfterCreate(property) || ownerEditable),
        unlink: ownerEditable && unlinkable(property, info)
    }
}

/**
 * Dá para tirar um item deste relacionamento pelo dono?
 *
 * No reverso, não quando o servidor recusaria — as mesmas duas regras com que a edição do dono trava o
 * campo (ver o `InstanceReverseRelInput`): o campo do outro lado é obrigatório, e ele ficaria sem; ou
 * o dono faz parte da CHAVE do outro lado, e tirá-lo mudaria a identidade dele. No múltiplo direto, a
 * única recusa possível — tirar o último de uma lista obrigatória — fica com o servidor, que a diz.
 */
export function unlinkable(property: IdeaProperty, info: ReverseInfo | null | undefined): boolean {
    if (!property.relationship) return false
    if (!property.relationship.reverse) return true
    return !(info && (info.ownerRequired || info.ownerIdentifier))
}

/**
 * O dono com outra lista neste relacionamento — o que vincular e desvincular mudaram, aplicado ao
 * dono que a tela já carregou, sem relê-lo (relato do usuário: tendo tudo na mão, não se vai ao
 * servidor). Para a ficha e para a página do quadro expandido, que mostram o mesmo relacionamento.
 *
 * O `_rels` é a verdade dos dois lados; no múltiplo direto o campo do próprio dono guarda os ids, e
 * acompanha. Objeto novo, e não mutação: é a troca de referência que faz a tela se redesenhar.
 */
export function withRelated(owner: any, property: IdeaProperty, items: any[]): any {
    const own = property.relationship?.reverse ? {} : {[property.alias]: items.map(item => item._instance)}
    return {...owner, ...own, _rels: {...(owner._rels || {}), [property.alias]: items}}
}

type OwnerContext = { system: string, idea: string, instance?: number | string }

/** A lista do relacionamento gravada pelo dono — a escrita que vincular e desvincular dividem. */
async function writeOwnerLinks(instances: OwnerUpdateApi, ownerContext: OwnerContext, property: IdeaProperty,
                               ids: number[]) {
    await instances.update(
        {systemAlias: ownerContext.system, ideaAlias: ownerContext.idea, instanceId: Number(ownerContext.instance)},
        {[property.alias]: ids})
}
